export const PRESENCE_ACCENT_IDS = [
  "red",
  "orange",
  "yellow",
  "green",
  "blue",
  "purple",
  "pink",
  "slate",
  "teal",
  "indigo",
  "fuchsia",
  "lime",
] as const;

export type PresenceAccentId = (typeof PRESENCE_ACCENT_IDS)[number];
export type PresenceTheme = "light" | "dark" | "plus";

type Accent = { id: PresenceAccentId; label: string; swatch: string; accent: string; strong: string };

export const PRESENCE_ACCENTS: Record<PresenceAccentId, Accent> = {
  red: { id: "red", label: "Rouge", swatch: "#dc2626", accent: "#dc2626", strong: "#b91c1c" },
  orange: { id: "orange", label: "Orange", swatch: "#ea580c", accent: "#ea580c", strong: "#c2410c" },
  yellow: { id: "yellow", label: "Jaune", swatch: "#ca8a04", accent: "#ca8a04", strong: "#a16207" },
  green: { id: "green", label: "Vert", swatch: "#16a34a", accent: "#16a34a", strong: "#15803d" },
  blue: { id: "blue", label: "Bleu", swatch: "#3b82f6", accent: "#3b82f6", strong: "#2563eb" },
  purple: { id: "purple", label: "Violet", swatch: "#7c3aed", accent: "#7c3aed", strong: "#6d28d9" },
  pink: { id: "pink", label: "Rose", swatch: "#db2777", accent: "#db2777", strong: "#be185d" },
  slate: { id: "slate", label: "Gris", swatch: "#475569", accent: "#475569", strong: "#334155" },
  teal: { id: "teal", label: "Turquoise", swatch: "#0d9488", accent: "#0d9488", strong: "#0f766e" },
  indigo: { id: "indigo", label: "Indigo", swatch: "#4f46e5", accent: "#4f46e5", strong: "#4338ca" },
  fuchsia: { id: "fuchsia", label: "Fuchsia", swatch: "#c026d3", accent: "#c026d3", strong: "#a21caf" },
  lime: { id: "lime", label: "Citron", swatch: "#65a30d", accent: "#65a30d", strong: "#4d7c0f" },
};

const THEME_KEY = "presence_theme";
const ACCENT_KEY = "presence_accent";

function normalizeAccent(value: string | null): PresenceAccentId {
  if (value && (PRESENCE_ACCENT_IDS as readonly string[]).includes(value)) return value as PresenceAccentId;
  return "purple";
}

export function readPresenceTheme(): PresenceTheme {
  try {
    const value = localStorage.getItem(THEME_KEY);
    if (value === "light" || value === "dark" || value === "plus") return value;
  } catch {
    /* ignore */
  }
  return "light";
}

export function readPresenceAccent(): PresenceAccentId {
  try {
    return normalizeAccent(localStorage.getItem(ACCENT_KEY));
  } catch {
    return "purple";
  }
}

export function applyPresenceTheme(theme: PresenceTheme, accentId: PresenceAccentId): void {
  const accent = PRESENCE_ACCENTS[normalizeAccent(accentId)];
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.setProperty("--color-violet", theme === "plus" ? "#7c3aed" : accent.accent);
  root.style.setProperty("--color-violet-dark", theme === "plus" ? "#5b21b6" : accent.strong);
}

export function persistPresenceTheme(theme: PresenceTheme, accentId: PresenceAccentId): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
    localStorage.setItem(ACCENT_KEY, accentId);
  } catch {
    /* ignore */
  }
  applyPresenceTheme(theme, accentId);
}
